import React from 'react'
import classNames from 'classnames'
import { HiOutlineCheckCircle } from 'react-icons/hi'
import { Button } from 'components/ui'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import es from 'dayjs/locale/es'

dayjs.extend(relativeTime)
dayjs.locale(es)

// CTA primario de una notificacion: "Marcar como leido" (pendiente) o chip
// "Leido . hace X" (post-accion).
//
// Renderizamos el mismo componente en dos lugares segun viewport (decision
// reunion Sabri 2026-05-26 + ajuste UX 2026-05-26):
//   - Desktop: dentro del header del hallazgo, al lado del menu de 3 puntos
//     (mode="header"). Pequeno, inline, siempre arriba.
//   - Mobile: sticky al pie del contenido principal (mode="mobileSticky"),
//     mismo lugar y comportamiento que el "Responder" del cuestionario activo.
//
// Si no aplica (no es informativa) el componente retorna null para que el
// caller pueda invocarlo sin guard extra.
const TicketPrimaryActions = ({
    isInformational,
    informationalIsRead,
    informationalReadAt,
    loadingStatus,
    canMarkAsRead = true,
    onMarkAsRead,
    mode = 'header',
}) => {
    const { t } = useTranslation()

    if (!isInformational) return null

    if (!informationalIsRead) {
        const isMobileSticky = mode === 'mobileSticky'
        // Header desktop: boton compacto inline. Mobile sticky: full-width al pie.
        const button = (
            <Button
                block={isMobileSticky}
                size={isMobileSticky ? 'md' : 'sm'}
                variant="solid"
                loading={loadingStatus}
                disabled={!canMarkAsRead}
                onClick={canMarkAsRead ? onMarkAsRead : undefined}
            >
                {t('mark_as_read')}
            </Button>
        )

        // Tooltip via title HTML nativo: el wrapper de Elstar Tooltip tiene
        // display:inline-flex y colapsa el ancho del boton block w-full
        // (gotcha del Pase 32). title nativo no agrega wrapper extra.
        const withTooltip = canMarkAsRead
            ? button
            : (
                <div
                    className={isMobileSticky ? 'w-full' : 'inline-flex'}
                    title={t('only_assigned_can_mark', 'Solo el asignado puede marcar como leído')}
                >
                    {button}
                </div>
            )

        if (isMobileSticky) {
            // fixed (no sticky): sticky se anclaba al div padre col-span-2 y
            // quedaba al final del scroll, no al fondo del viewport. fixed
            // garantiza visibilidad al pie. paddingBottom inline (no clase
            // Tailwind arbitraria) porque el JIT puede no levantar el max()
            // + env() complejo al hot-reload, dejando el boton pegado al
            // borde inferior / a la URL bar del browser mobile.
            return (
                <div
                    className="fixed inset-x-0 bottom-0 z-30 px-4 pt-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-[0_-2px_8px_rgba(0,0,0,0.04)]"
                    style={{ paddingBottom: 'max(1rem, calc(env(safe-area-inset-bottom) + 0.5rem))' }}
                >
                    {withTooltip}
                </div>
            )
        }
        return withTooltip
    }

    // Estado leido: chip persistente con timestamp relativo del log de cierre.
    const readChip = (
        <div
            className={classNames(
                'inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300',
                mode === 'mobileSticky' && 'fixed inset-x-0 bottom-0 z-30 w-full justify-center px-4 pt-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-[0_-2px_8px_rgba(0,0,0,0.04)]',
            )}
            style={mode === 'mobileSticky'
                ? { paddingBottom: 'max(1rem, calc(env(safe-area-inset-bottom) + 0.5rem))' }
                : undefined
            }
        >
            <HiOutlineCheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
            <span>
                {t('read_label')}
                {informationalReadAt && (
                    <>
                        <span className="mx-1.5 text-gray-400">·</span>
                        <span>{dayjs(informationalReadAt).fromNow()}</span>
                    </>
                )}
            </span>
        </div>
    )
    return readChip
}

export default TicketPrimaryActions
